// src/utils/seedDemoData.js
// ============================================================
// DEMO DATA SEEDER
// Creates a realistic test property "Sunrise Heights - 200 Units"
// with 25 tenants spread across all 5 risk behavior patterns.
// Designed for Phoenix-market multifamily demos.
// ============================================================

import { addDoc, collection, getDocs, query, where, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Today: March 17, 2026
// All dates are computed relative to this

/**
 * Generate an ISO date string for a given number of months ago at a specific day
 * @param {number} monthsAgo - How many months before today (0 = current month)
 * @param {number} dayOfMonth - Day of month (1-28)
 */
function makeDate(monthsAgo, dayOfMonth) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  const targetMonth = month - monthsAgo;
  // Use Date constructor which handles negative months correctly
  const d = new Date(year, targetMonth, dayOfMonth);
  return d.toISOString();
}

// ---- GROUP 1: Always On Time (risk 0-29, green "Minimal") ----
// Consistently pay day 1-3, no balances, zero drama
const GROUP1_TENANTS = [
  {
    name: 'Sarah Chen',
    unit: '101',
    monthlyRent: 1850,
    currentBalance: 0,
    phone: '(480) 555-2341',
    email: 'schen@gmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildOnTimeHistory(1850, 10),
  },
  {
    name: 'David Park',
    unit: '102',
    monthlyRent: 2200,
    currentBalance: 0,
    phone: '(602) 555-8817',
    email: 'david.park@outlook.com',
    preferredLanguage: 'en',
    paymentHistory: buildOnTimeHistory(2200, 12),
  },
  {
    name: 'Jennifer Liu',
    unit: '108',
    monthlyRent: 1650,
    currentBalance: 0,
    phone: '(480) 555-4492',
    email: 'jliu@yahoo.com',
    preferredLanguage: 'en',
    paymentHistory: buildOnTimeHistory(1650, 8),
  },
  {
    name: 'Michael Torres',
    unit: '115',
    monthlyRent: 2400,
    currentBalance: 0,
    phone: '(623) 555-7731',
    email: 'mjtorres@gmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildOnTimeHistory(2400, 10),
  },
  {
    name: 'Emma Johnson',
    unit: '122',
    monthlyRent: 1500,
    currentBalance: 0,
    phone: '(602) 555-3318',
    email: '',
    preferredLanguage: 'en',
    paymentHistory: buildOnTimeHistory(1500, 6),
  },
];

// ---- GROUP 2: Consistently Late 5-10 Days (risk 30-59, gray "Low Risk") ----
// Always pays, but always 5-10 days late. Already paid this month.
const GROUP2_TENANTS = [
  {
    name: 'Carlos Mendez',
    unit: '103',
    monthlyRent: 1750,
    currentBalance: 0,
    phone: '(480) 555-9934',
    email: 'carlos.m@gmail.com',
    preferredLanguage: 'es',
    paymentHistory: buildChronicLateHistory(1750, 10, 6, 9),
  },
  {
    name: 'Ashley Williams',
    unit: '109',
    monthlyRent: 1950,
    currentBalance: 0,
    phone: '(602) 555-1127',
    email: 'awilliams@hotmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildChronicLateHistory(1950, 10, 7, 10),
  },
  {
    name: 'James Rodriguez',
    unit: '116',
    monthlyRent: 1650,
    currentBalance: 0,
    phone: '(623) 555-4450',
    email: '',
    preferredLanguage: 'es',
    paymentHistory: buildChronicLateHistory(1650, 12, 5, 8),
  },
  {
    name: 'Maria Garcia',
    unit: '123',
    monthlyRent: 2100,
    currentBalance: 0,
    phone: '(480) 555-6623',
    email: 'mgarcia22@gmail.com',
    preferredLanguage: 'es',
    paymentHistory: buildChronicLateHistory(2100, 8, 6, 10),
  },
  {
    name: 'Kevin Brown',
    unit: '130',
    monthlyRent: 1800,
    currentBalance: 0,
    phone: '(602) 555-8843',
    email: 'kbrown@icloud.com',
    preferredLanguage: 'en',
    paymentHistory: buildChronicLateHistory(1800, 10, 7, 9),
  },
];

// ---- GROUP 3: Worsening Drift (risk 60-84, orange "Medium Risk") ----
// Started paying on day 3-5 six months ago, now paying day 12-18 and getting worse.
// Have partial payments in recent months + unpaid March.
const GROUP3_TENANTS = [
  {
    name: 'Robert Smith',
    unit: '104',
    monthlyRent: 1800,
    currentBalance: 4000, // partial Jan + partial Feb + all of March unpaid
    phone: '(480) 555-2278',
    email: 'rsmith@gmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildWorseningHistory(1800),
  },
  {
    name: 'Patricia Lopez',
    unit: '110',
    monthlyRent: 1650,
    currentBalance: 3650,
    phone: '(623) 555-9918',
    email: 'plopez@yahoo.com',
    preferredLanguage: 'es',
    paymentHistory: buildWorseningHistory(1650),
  },
  {
    name: 'Daniel Martinez',
    unit: '117',
    monthlyRent: 2000,
    currentBalance: 4400,
    phone: '(602) 555-7742',
    email: '',
    preferredLanguage: 'es',
    paymentHistory: buildWorseningHistory(2000),
  },
  {
    name: 'Lisa Anderson',
    unit: '124',
    monthlyRent: 1950,
    currentBalance: 4300,
    phone: '(480) 555-5519',
    email: 'lisa.anderson@gmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildWorseningHistory(1950),
  },
  {
    name: 'Christopher Davis',
    unit: '131',
    monthlyRent: 1700,
    currentBalance: 3750,
    phone: '(602) 555-3385',
    email: 'cdavis@outlook.com',
    preferredLanguage: 'en',
    paymentHistory: buildWorseningHistory(1700),
  },
];

// ---- GROUP 4: High Risk / Severely Delinquent (risk 85-100, red "High Risk") ----
// Large outstanding balances, multiple missed months, partial payments.
// Last actual payment was months ago.
const GROUP4_TENANTS = [
  {
    name: 'Marcus Thompson',
    unit: '105',
    monthlyRent: 2100,
    currentBalance: 6300, // 3 months owed
    phone: '(602) 555-4411',
    email: '',
    preferredLanguage: 'en',
    paymentHistory: buildHighRiskHistory(2100, 6300),
  },
  {
    name: 'Rosa Gutierrez',
    unit: '111',
    monthlyRent: 1800,
    currentBalance: 5400,
    phone: '(480) 555-8872',
    email: 'rosa.g@gmail.com',
    preferredLanguage: 'es',
    paymentHistory: buildHighRiskHistory(1800, 5400),
  },
  {
    name: 'Antoine Jackson',
    unit: '118',
    monthlyRent: 2400,
    currentBalance: 7200, // 3 missed months
    phone: '(623) 555-1196',
    email: 'ajackson@hotmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildHighRiskHistory(2400, 7200),
  },
  {
    name: 'Svetlana Petrov',
    unit: '125',
    monthlyRent: 2800,
    currentBalance: 4200, // 1.5 months (partial paid recently)
    phone: '(480) 555-6634',
    email: 'svetlana.p@gmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildHighRiskHistory(2800, 4200),
  },
  {
    name: 'James Walker',
    unit: '132',
    monthlyRent: 1650,
    currentBalance: 4950,
    phone: '(602) 555-9978',
    email: '',
    preferredLanguage: 'en',
    paymentHistory: buildHighRiskHistory(1650, 4950),
  },
];

// ---- GROUP 5: Mixed Patterns (various risk tiers) ----
const GROUP5_TENANTS = [
  {
    // Has an active payment plan — was high risk but enrolled in plan last month
    name: 'Nina Patel',
    unit: '106',
    monthlyRent: 1750,
    currentBalance: 1750, // Reduced from $3,500 via plan
    phone: '(480) 555-7723',
    email: 'npatel@gmail.com',
    preferredLanguage: 'en',
    activePaymentPlan: true,
    paymentHistory: buildMixedHistory_PaymentPlan(1750),
  },
  {
    // Was high risk, now improving — started paying on time 3 months ago
    name: "Brian O'Connor",
    unit: '112',
    monthlyRent: 2000,
    currentBalance: 0,
    phone: '(623) 555-4418',
    email: 'brian.oc@icloud.com',
    preferredLanguage: 'en',
    paymentHistory: buildMixedHistory_Improving(2000),
  },
  {
    // Just started being late for the first time ever — always paid day 1-2, now 12 days late
    name: 'Yolanda Cruz',
    unit: '119',
    monthlyRent: 1550,
    currentBalance: 1550, // First-time late, whole March balance
    phone: '(602) 555-3391',
    email: 'yolanda.cruz@yahoo.com',
    preferredLanguage: 'es',
    paymentHistory: buildMixedHistory_FirstTimeLate(1550),
  },
  {
    // Partial payments mixed with on-time months — sporadic cash flow issues
    name: 'Steven Kim',
    unit: '126',
    monthlyRent: 2200,
    currentBalance: 1650, // Partial from recent months
    phone: '(480) 555-5561',
    email: 'skim2@gmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildMixedHistory_Sporadic(2200),
  },
  {
    // Mostly good, occasional lateness — borderline low/minimal
    name: 'Fatima Al-Hassan',
    unit: '133',
    monthlyRent: 1900,
    currentBalance: 0,
    phone: '(623) 555-8847',
    email: 'fhassan@gmail.com',
    preferredLanguage: 'en',
    paymentHistory: buildMixedHistory_Occasional(1900),
  },
];

// ============================================================
// HISTORY BUILDER FUNCTIONS
// ============================================================

/**
 * Group 1: Always on time (day 1-3)
 * Includes current month (already paid)
 */
function buildOnTimeHistory(rent, months) {
  const history = [];
  // Include months from `months` ago down to 0 (current month already paid)
  for (let m = months; m >= 0; m--) {
    const dayPaid = Math.floor(Math.random() * 3) + 1; // day 1, 2, or 3
    history.push({
      date: makeDate(m, dayPaid),
      amount: rent,
      daysLate: dayPaid - 1, // day 1 = 0 late, day 2 = 1 late, day 3 = 2 late
    });
  }
  return history;
}

/**
 * Group 2: Chronically late 5-10 days — already paid this month
 * @param {number} minDays - minimum days late each month
 * @param {number} maxDays - maximum days late each month
 */
function buildChronicLateHistory(rent, months, minDays, maxDays) {
  const history = [];
  // Include current month — they already paid (day 8-10 typically)
  for (let m = months; m >= 0; m--) {
    const daysLate = Math.floor(Math.random() * (maxDays - minDays + 1)) + minDays;
    const dayPaid = daysLate + 1; // day paid = daysLate + 1 (rent due day 1)
    history.push({
      date: makeDate(m, dayPaid),
      amount: rent,
      daysLate,
    });
  }
  return history;
}

/**
 * Group 3: Worsening drift
 * Starts paying day 3-5 ten months ago, worsening to day 15-18 recently.
 * Jan and Feb had partial payments. March not paid.
 */
function buildWorseningHistory(rent) {
  // Pattern: daysLate increases from ~2 to ~17 over 10 months
  const daysLatePattern = [2, 3, 4, 5, 7, 8, 10, 12, 14, 17];
  const history = [];

  for (let i = 0; i < daysLatePattern.length; i++) {
    const monthsAgo = 10 - i; // month 10 = oldest, month 1 = most recent in this list
    const daysLate = daysLatePattern[i];
    const dayPaid = daysLate + 1;

    let amount;
    // Last 2 months: partial payments (getting worse financially)
    if (monthsAgo === 2) {
      amount = Math.round(rent * 0.55); // ~55% paid in month 2 ago (Jan)
    } else if (monthsAgo === 1) {
      amount = Math.round(rent * 0.35); // ~35% paid in month 1 ago (Feb)
    } else {
      amount = rent; // full rent in older months
    }

    history.push({
      date: makeDate(monthsAgo, dayPaid),
      amount,
      daysLate,
    });
  }
  // March (monthsAgo=0): NOT in history — unpaid, reflected in currentBalance
  return history;
}

/**
 * Group 4: High risk / severely delinquent
 * Multiple missed months, some very small partial payments.
 * Last meaningful payment was in November (4+ months ago).
 */
function buildHighRiskHistory(rent, currentBalance) {
  const history = [];

  // Months 10-6 ago: paid on time or slightly late
  for (let m = 10; m >= 6; m--) {
    const daysLate = Math.floor(Math.random() * 5); // 0-4 days late
    history.push({
      date: makeDate(m, daysLate + 1),
      amount: rent,
      daysLate,
    });
  }

  // Month 5 ago: first sign of trouble — paid late
  history.push({
    date: makeDate(5, 14),
    amount: rent,
    daysLate: 13,
  });

  // Month 4 ago (November): partial payment — this is the last "real" payment
  history.push({
    date: makeDate(4, 18),
    amount: Math.round(rent * 0.45),
    daysLate: 17,
  });

  // Months 3, 2, 1 ago (Dec, Jan, Feb): MISSED — no entry (or $0)
  history.push({
    date: makeDate(3, 28),
    amount: 0,
    daysLate: 30, // entire month missed
  });
  history.push({
    date: makeDate(2, 28),
    amount: 0,
    daysLate: 30,
  });
  history.push({
    date: makeDate(1, 28),
    amount: 0,
    daysLate: 30,
  });

  // March (current): not paid — reflected in currentBalance
  return history;
}

/**
 * Group 5a: Active payment plan — was struggling, enrolled in plan
 */
function buildMixedHistory_PaymentPlan(rent) {
  const history = [];

  // Months 10-7 ago: on time
  for (let m = 10; m >= 7; m--) {
    history.push({ date: makeDate(m, 2), amount: rent, daysLate: 1 });
  }
  // Month 6-4 ago: getting late
  history.push({ date: makeDate(6, 10), amount: rent, daysLate: 9 });
  history.push({ date: makeDate(5, 15), amount: Math.round(rent * 0.7), daysLate: 14 });
  history.push({ date: makeDate(4, 20), amount: Math.round(rent * 0.5), daysLate: 19 });

  // Month 3 ago: enrolled in payment plan — partial payment
  history.push({ date: makeDate(3, 8), amount: Math.round(rent * 0.5), daysLate: 7 });
  // Month 2 ago: making plan payment
  history.push({ date: makeDate(2, 5), amount: Math.round(rent * 0.75), daysLate: 4 });
  // Month 1 ago: on track
  history.push({ date: makeDate(1, 3), amount: rent, daysLate: 2 });
  // This month: partial installment paid (per plan)
  history.push({ date: makeDate(0, 10), amount: Math.round(rent * 0.5), daysLate: 9 });

  return history;
}

/**
 * Group 5b: Was high risk, now improving
 */
function buildMixedHistory_Improving(rent) {
  const history = [];

  // Months 10-7 ago: chronic late/partial
  history.push({ date: makeDate(10, 18), amount: Math.round(rent * 0.6), daysLate: 17 });
  history.push({ date: makeDate(9, 15), amount: Math.round(rent * 0.7), daysLate: 14 });
  history.push({ date: makeDate(8, 20), amount: 0, daysLate: 30 });
  history.push({ date: makeDate(7, 12), amount: Math.round(rent * 0.8), daysLate: 11 });

  // Turning point: months 6-4 ago
  history.push({ date: makeDate(6, 8), amount: rent, daysLate: 7 });
  history.push({ date: makeDate(5, 5), amount: rent, daysLate: 4 });
  history.push({ date: makeDate(4, 3), amount: rent, daysLate: 2 });

  // Months 3-0: now fully on time
  history.push({ date: makeDate(3, 2), amount: rent, daysLate: 1 });
  history.push({ date: makeDate(2, 1), amount: rent, daysLate: 0 });
  history.push({ date: makeDate(1, 2), amount: rent, daysLate: 1 });
  history.push({ date: makeDate(0, 2), amount: rent, daysLate: 1 }); // this month already paid

  return history;
}

/**
 * Group 5c: Perfect history, first time ever late this month
 */
function buildMixedHistory_FirstTimeLate(rent) {
  const history = [];
  // 10 months of perfect payments
  for (let m = 10; m >= 1; m--) {
    history.push({ date: makeDate(m, 1), amount: rent, daysLate: 0 });
  }
  // This month: NOT paid yet — reflected in currentBalance
  return history;
}

/**
 * Group 5d: Sporadic — partial payments mixed with on-time
 */
function buildMixedHistory_Sporadic(rent) {
  const history = [];

  history.push({ date: makeDate(10, 3), amount: rent, daysLate: 2 });
  history.push({ date: makeDate(9, 1), amount: rent, daysLate: 0 });
  history.push({ date: makeDate(8, 10), amount: Math.round(rent * 0.7), daysLate: 9 }); // partial
  history.push({ date: makeDate(7, 2), amount: rent, daysLate: 1 });
  history.push({ date: makeDate(6, 1), amount: rent, daysLate: 0 });
  history.push({ date: makeDate(5, 14), amount: Math.round(rent * 0.6), daysLate: 13 }); // partial
  history.push({ date: makeDate(4, 3), amount: rent, daysLate: 2 });
  history.push({ date: makeDate(3, 18), amount: Math.round(rent * 0.8), daysLate: 17 }); // partial
  history.push({ date: makeDate(2, 8), amount: rent, daysLate: 7 });
  // Month 1 ago: partial — leaves $1,100 unpaid
  history.push({ date: makeDate(1, 12), amount: Math.round(rent * 0.5), daysLate: 11 }); // partial

  // Current month: not paid — total currentBalance = $1,100 unpaid Feb + $2,200 Mar = $3,300
  // But we set currentBalance: 1650 above — just partial from recent months

  return history;
}

/**
 * Group 5e: Mostly good, occasional lateness
 */
function buildMixedHistory_Occasional(rent) {
  const history = [];

  for (let m = 10; m >= 0; m--) {
    // 80% chance of on-time, 20% chance of 3-8 days late
    const isLate = Math.random() < 0.2;
    const daysLate = isLate ? Math.floor(Math.random() * 6) + 3 : Math.floor(Math.random() * 2);
    history.push({
      date: makeDate(m, daysLate + 1),
      amount: rent,
      daysLate,
    });
  }

  return history;
}


// ============================================================
// MAIN SEED FUNCTION
// ============================================================

/**
 * Seeds demo data into Firestore for the given user.
 * Creates property + 25 tenants with full payment history.
 * @param {string} userId - Firebase auth user ID
 * @param {Function} onProgress - callback(message) for progress updates
 * @returns {string} - The created property ID
 */
export async function seedDemoData(userId, onProgress = () => {}) {
  try {
    onProgress('Creating property "Sunrise Heights"...');

    // Create the demo property
    const propertyRef = await addDoc(collection(db, 'properties'), {
      name: 'Sunrise Heights — 200 Units',
      address: '4800 N 44th St, Phoenix, AZ 85018',
      units: 200,
      ownerId: userId,
      createdAt: new Date().toISOString(),
      isDemo: true,
    });

    const propertyId = propertyRef.id;
    onProgress(`Property created (ID: ${propertyId}). Adding tenants...`);

    // Combine all 25 tenants
    const allTenants = [
      ...GROUP1_TENANTS,
      ...GROUP2_TENANTS,
      ...GROUP3_TENANTS,
      ...GROUP4_TENANTS,
      ...GROUP5_TENANTS,
    ];

    // Add tenants in batches
    let count = 0;
    for (const tenant of allTenants) {
      await addDoc(collection(db, 'properties', propertyId, 'tenants'), {
        ...tenant,
        createdAt: new Date().toISOString(),
        outreachLog: [],
      });
      count++;
      if (count % 5 === 0) {
        onProgress(`Added ${count}/25 tenants...`);
      }
    }

    onProgress(`Done! 25 tenants added to Sunrise Heights.`);
    return propertyId;

  } catch (err) {
    console.error('Seed error:', err);
    throw err;
  }
}

/**
 * Remove all demo properties (isDemo === true) for a user
 * Used to reset/reseed
 */
export async function clearDemoData(userId) {
  const q = query(
    collection(db, 'properties'),
    where('ownerId', '==', userId),
    where('isDemo', '==', true)
  );
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    // Delete tenant subcollection docs first
    const tenantsSnap = await getDocs(collection(db, 'properties', d.id, 'tenants'));
    for (const t of tenantsSnap.docs) {
      await deleteDoc(doc(db, 'properties', d.id, 'tenants', t.id));
    }
    await deleteDoc(doc(db, 'properties', d.id));
  }
}
